// G360_K470 - sort, filter, column choice and CSV export (the shared DataTable).
// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Admin - Integration / SSO requests. Customers click "Request setup" in their
// console (status='guided'); those land here for the team to action.
import { useEffect, useState } from 'react';
import { Plug, Check, X, Building2 } from 'lucide-react';
import { listIntegrationRequests, resolveIntegration } from '../lib/integrationRequestsApi.js';
import { EmptyState } from '@govern360/shared/components/ui.jsx';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

const CAT_LABEL = { sso: 'SSO / Identity', grc: 'GRC', ticketing: 'Ticketing', network: 'Network', siem: 'SIEM', ai: 'AI', alerts: 'Alerts' };

const COLS = [
  { key: 'org_name', label: 'Organization', render: (r) => <><Building2 size={14} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--g-text-2)' }} /><strong>{r.org_name}</strong></> },
  { key: 'provider', label: 'Connector', render: (r) => <span style={{ textTransform: 'capitalize' }}>{r.provider}</span> },
  { key: 'category', label: 'Category', value: (r) => CAT_LABEL[r.category] || r.category, render: (r) => <span className="pill p-gray">{CAT_LABEL[r.category] || r.category}</span> },
  { key: 'requested_at', label: 'Requested', value: (r) => (r.requested_at ? new Date(r.requested_at).toLocaleDateString() : null),
    sort: (r) => (r.requested_at ? new Date(r.requested_at).getTime() : null), csv: (r) => r.requested_at || '' },
];

export default function IntegrationRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  async function refresh() {
    setLoading(true);
    const { data, error } = await listIntegrationRequests();
    setLoadErr(error ? (error.message || 'the read failed') : '');
    setRows(error ? [] : data);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function resolve(r, action) {
    const verb = action === 'done' ? 'mark as connected' : 'dismiss';
    if (!confirm(`${verb[0].toUpperCase()}${verb.slice(1)} ${r.provider} for ${r.org_name}?`)) return;
    setBusy(`${r.tenant_id}:${r.provider}`);
    const { error } = await resolveIntegration(r.tenant_id, r.provider, action);
    setBusy(null);
    if (error) { alert(error.message); return; }
    refresh();
  }

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Integration requests</h1>
          <p className="ph-sub">SSO / GRC / ticketing connectors customers requested setup help with. Action them, then mark connected.</p>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="banner admin">
          <Plug size={18} />
          <span><strong>{rows.length}</strong> open request{rows.length > 1 ? 's' : ''} from customers.</span>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading&hellip;</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>Integration requests could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Plug} title="No open requests" hint="When a customer requests SSO or a guided-setup connector, it shows here." />
      ) : (
        <DataTable rows={rows} columns={COLS} storageKey="g360.admin.intreq.hiddenCols" name="integration-requests"
          placeholder="Filter requests..." rowKey={(r) => `${r.tenant_id}:${r.provider}`}
          actions={(r) => (
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn admin-primary" style={{ height: 28, padding: '0 10px', fontSize: 11 }}
                onClick={() => resolve(r, 'done')} disabled={busy === `${r.tenant_id}:${r.provider}`}>
                <Check size={14} />Mark connected
              </button>
              <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11 }}
                onClick={() => resolve(r, 'dismiss')} disabled={busy === `${r.tenant_id}:${r.provider}`}>
                <X size={14} />Dismiss
              </button>
            </div>
          )} />
      )}
    </div>
  );
}
